import NoData from "../../../components/noData/noData.component";
import { useCommanData } from "../../../contexts/commanData.context";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserContext } from "../../../contexts/user.context";
import {
  Delete,
  Get,
  Patch,
  SendFormData,
  downloadImage,
  getExelTemplate,
  handleError,
} from "../../../utils/fetch.util";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import i18next from "i18next";
import ConfirmationModal from "../../../components/modals/confirmationModal/confirmationModal.component";
import { useTranslation } from "react-i18next";
import BackButton from "../../../components/modals/AddAssetModal/BackButton/BackButton";
import AddSiteModal from "../../../components/modals/AddSiteModal/AddSiteModal.component";
import EditSiteModal from "../../../components/modals/EditSiteModal/EditSiteModal.component";
import Image from "../../../components/Image/Image.component";
import { toast } from "react-toastify";
import LinkSiteToProvidersModel from "../../../components/modals/linkSiteToProvidersModel/LinkSiteToProvidersModel.component";
import { ViewJobRolesModal } from "../../../components/modals/ViewJobRolesModal/ViewJobRolesModal.component";
import { DndContext, closestCorners } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import SingleSite from "../../../components/singleSite/SingleSite.component";
import StaffGPSUsersEditMap from "../StaffGPSUsersEditMap/StaffGPSUsersEditMap.route";

interface SiteData {
  site_id: number;
  site_name: string;
  logo_url: string;
  site_url: string | null;
  site_serial: string;
  createdAt: string;
  updatedAt: string;
  qrcode: string;
  qrcode_url: string;
  site_locations: any;
  deleted: boolean;
  site_index: number;
  deleted_sector_id: number | null;
  asdc_client_sector_id: number;
  client_id: number;
  country_id: number;
  points?: number;
}

const AdminSites = () => {
  const {
    setOptions,
    setRecycleBin,
    API_LINK,
  } = useCommanData();
  const { getValidToken, getProfile } =
    useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const siteIdToDelete = useRef(0);
  const sectorIdToDelete = useRef(0);
  const { sectorId } = useParams();
  const [sites, setSites] = useState<SiteData[]>([]);
  const [oldSites, setOldSites] = useState<SiteData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAddSiteModalOpen, setIsAddSiteModalOpen] = useState<boolean>(false);
  const [isEditSiteModalOpen, setIsEditSiteModalOpen] = useState<boolean>(false);
  const [openLinkSiteToProviderModel, setOpenLinkSiteToProviderModel] =
    useState<boolean>(false);
  const [toggleViewJobRolesModal, setToggleViewJobRolesModal] =
    useState<any>(null);
  const [supervisors, setSupervisors] = useState<any[] | null>(null);
  const [supervisorModal, setSupervisorModal] = useState<number | null>(null);
  const [isQrOpen, setIsQrOpen] = useState(false);
  const [qrcode, setQrcode] = useState<string>("");
  const [qrcodeValue, setQrcodeValue] = useState<string>("");
  const [isSiteConfirmationModalOpen, setIsSiteConfirmationModalOpen] =
    useState<boolean>(false);
  const [excelLoading, setExcelLoading] = useState<boolean>(false);
  const [uploadExcelModalOpen, setUploadExcelModalOpen] =
    useState<boolean>(false);
  const [siteId, setSitedId] = useState<number>(0);
  const [site, setSite] = useState<any>({});
  const [selectedSiteForMap, setSelectedSiteForMap] = useState<SiteData | null>(null);
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id");

  const openSiteModal = () => {
    setIsAddSiteModalOpen(true);
  };

  const handleSiteMapClick = (site: SiteData) => {
    setSelectedSiteForMap(site);
  };

  const openSiteConfirmationModal = (sectorId: number, siteId: number) => {
    sectorIdToDelete.current = sectorId;
    siteIdToDelete.current = siteId;
    setIsSiteConfirmationModalOpen(true);
  };

  const deleteSite = async () => {
    setIsLoading(true);

    const response = await Delete(
      `sites/delete-site?asdc_client_site_id=${siteIdToDelete.current}&asdc_client_sector_id=${sectorIdToDelete.current}&asdc_client_id=${clientId}`,
      getValidToken()
    );
    setIsLoading(false);
    if (response.error || !response.data) {
      handleError(response);
      return;
    }

    // Remove the deleted site from the local state
    setSites(prevSites => prevSites.filter(site => site.site_id !== siteIdToDelete.current));
    setOldSites(prevSites => prevSites.filter(site => site.site_id !== siteIdToDelete.current));

    getProfile();
    setIsSiteConfirmationModalOpen(false);
    toast.success(t("site_deleted_successfully"));
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = sites.findIndex((site) => site.site_id === +active.id);
    const newIndex = sites.findIndex((site) => site.site_id === +over.id);
    setSites(arrayMove(sites, oldIndex, newIndex));
  };

  const downloadImages = useCallback(async () => {
    const downloadPromises = sites.map((site) => {
      const url = `${API_LINK}${site.qrcode_url}`;
      const filename = `${site.site_name}.jpg`;
      return downloadImage(url, filename);
    });
    await Promise.all(downloadPromises);
  }, [sites, API_LINK]);

  const SortSites = async (sites: SiteData[]) => {
    const res = await Patch(
      `sites/update-site-index`,
      {
        sites_ids: sites.map((site: SiteData) => site.site_id),
      },
      getValidToken()
    );
    if (res.error || !res.data) {
      handleError(res);
      return;
    }
    toast.success(t("sites_sorted_successfully"));
  };

  const getSupervisors = async () => {
    const res = await Get(
      `supervisor/get-site-supervisees?site_id=${supervisorModal}`,
      getValidToken()
    );
    if (res.error || !res.data) {
      handleError(res);
      return;
    }
    setSupervisors(res.data);
  };

  const handleCopyClick = (qr: string) => {
    const textField = document.createElement("textarea");
    textField.innerText = qr;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    document.body.removeChild(textField);
    toast.success(t("copied"));
  };

  const getSites = async () => {
    if (!clientId) return;

    setIsLoading(true);
    const res = await Get(
      `sites/get-all-sites?asdc_client_id=${clientId}&asdc_client_sector_id=${sectorId}`,
      getValidToken()
    );
    setIsLoading(false);

    if (res.error || !res.data) {
      handleError(res);
      return;
    }

    setSites(res.data);
    setOldSites(res.data);
  };

  useEffect(() => {
    document.title = "Admin Sites";
    setRecycleBin("deleted-sites");

    return () => {
      setRecycleBin("");
    };
  }, [setRecycleBin]);

  useEffect(() => {
    supervisorModal && getSupervisors();
  }, [supervisorModal]);

  useEffect(() => {
    setOptions(
      [
        {
          label: t("download_excel_template"),
          clickHandler: () =>
            getExelTemplate("excel-download/get-Site-excel", getValidToken()),
        },
        {
          label: t("upload_excel"),
          clickHandler: () => setUploadExcelModalOpen(true),
        },
        {
          label: t("download_all_qrcodes"),
          clickHandler: () => {
            downloadImages();
          },
        },
      ].filter(Boolean) as any[]
    );

    return () => {
      setOptions([]);
    };
  }, [downloadImages, t, getValidToken, setOptions]);

  useEffect(() => {
    getSites();
  }, [clientId]);

  return (
    <>
      <div className="container bg-white p-6 px-8">
        {/* Head */}
        <BackButton cb={() => navigate(-1)} />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-5xl mt-2 mb-2">
              {t("sites")}
              <span className="text-xl ml-3">
                ({sites.length})
              </span>
            </h1>
            
            {/* <div className="path mb-8 flex items-center gap-1">
              <span className="text-[22px] ">
                {t("sites")}
              </span>
            </div> */}
          </div>

          <div className="flex items-center gap-5 mb-5">
            {
              <button
                onClick={openSiteModal}
                className="main_btn"
              >
                {t("add_site")}
              </button>
            }
            {sites !== oldSites && (
              <button
                className="main_btn"
                onClick={() => {
                  SortSites(sites);
                  setOldSites(sites);
                }}
              >
                {t("saveBtn")}
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
          </div>
        ) : sites.length === 0 ? (
          <NoData message={t("no_sites_found")} />
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="rounded-md w-full">
              <thead>
                <tr
                  className={`${i18next.language == "en" ? "text-left" : "text-right"
                    }`}
                >
                  <th className="border-2 p-4 py-2 min-w-[150px]">
                    {t("structure_site_serial")}
                  </th>
                  <th className="border-2 p-4 py-2 min-w-[150px]">
                    {t("structure_site_image")}
                  </th>
                  <th className="border-2 p-4 py-2 min-w-[150px]">
                    {t("structure_site_title")}
                  </th>
                  {/* <th className="border-2 p-4 py-2 min-w-[150px]">
                    {t("structure_site_points_number") +
                      "   ( " +
                      userData![0]?.asdc_client_contract
                        ?.inspection_points_count +
                      " / " +
                      (userData![0]?.asdc_client_contract
                        ?.inspection_points_count -
                        userData![0]?.asdc_client_contract?.pointsCount) +
                      " )"}
                  </th> */}
                  <th className="border-2 p-4 py-2 min-w-[150px]">
                    {t("qrcode")}
                  </th>
                  {/* {
                    <th className="border-2 p-4 py-2 min-w-[150px]">
                      {t("structure_sector_floors")}
                    </th>
                  } */}
                  {/* {
                    <th className="border-2 p-4 py-2 min-w-[150px]">
                      {t("stocking_assets")}
                    </th>
                  } */}
                  {/* {
                    <th className="border-2 p-4 py-2 min-w-[150px]">
                      {t("job_roles")}
                    </th>
                  } */}
                  {/* <th className="border-2 p-4 py-2 min-w-[150px]">
                    {t("supervisors")}
                  </th> */}

                  {
                    <th className="border-2 p-4 py-2 min-w-[150px]">
                      {t("actions")}
                    </th>
                  }
                  {/* {
                    <th className="border-2 p-4 py-2 min-w-[150px]">
                      {t("sort")}
                    </th>
                  } */}
                </tr>
              </thead>
              <tbody>
                <tr className="h-6">
                  <td></td>
                </tr>
                <DndContext
                  onDragEnd={handleDragEnd}
                  collisionDetection={closestCorners}
                >
                  <SortableContext
                    items={sites?.map((site) => ({
                      ...site,
                      id: site?.site_id?.toString(),
                    }))}
                    strategy={verticalListSortingStrategy}
                  >
                    {sites?.map((site: SiteData, i: number) => {
                      if (!site.deleted) {
                        return (
                          <SingleSite
                            site={site}
                            key={i}
                            sectorId={site.asdc_client_sector_id.toString()}
                            setIsQrOpen={setIsQrOpen}
                            setQrcode={setQrcode}
                            setQrcodeValue={setQrcodeValue}
                            setToggleViewJobRolesModal={
                              setToggleViewJobRolesModal
                            }
                            openSiteConfirmationModal={
                              openSiteConfirmationModal
                            }
                            setIsEditSiteModalOpen={setIsEditSiteModalOpen}
                            setSitedId={setSitedId}
                            setOpenLinkSiteToProviderModel={
                              setOpenLinkSiteToProviderModel
                            }
                            setSite={setSite}
                            setSupervisorModal={setSupervisorModal}
                            isAdmin={true}
                            onMapClick={handleSiteMapClick}
                          />
                        );
                      }
                      return null;
                    })}
                  </SortableContext>
                </DndContext>
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddSiteModal
        isOpen={isAddSiteModalOpen}
        onClose={() => setIsAddSiteModalOpen(false)}
        toggleModal={() => setIsAddSiteModalOpen(!isAddSiteModalOpen)}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        getSectors={() => getSites()}
      />

      <EditSiteModal
        isOpen={isEditSiteModalOpen}
        onClose={() => setIsEditSiteModalOpen(false)}
        toggleModal={() => setIsEditSiteModalOpen(!isEditSiteModalOpen)}
        setSectors={() => { }}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        site_id={siteId}
        setSitedId={setSitedId}
        getSectors={() => getSites()}
      />

      <ViewJobRolesModal
        isOpen={toggleViewJobRolesModal !== null}
        close={() => setToggleViewJobRolesModal(null)}
        sectorId={0}
        siteId={+toggleViewJobRolesModal}
      />
      {/*       
      <LinkSiteToProvidersModel
        isOpen={openLinkSiteToProviderModel}
        onClose={() => {
          setOpenLinkSiteToProviderModel(false);
          setSite({});
        }}
        site_id={siteId}
        site={site}
      /> */}

      <ConfirmationModal
        title={t("delete_site")}
        message={t("confirm_site_delete")}
        isLoading={isLoading}
        onConfirm={deleteSite}
        closeConfirmationModal={() => setIsSiteConfirmationModalOpen(false)}
        isShown={isSiteConfirmationModalOpen}
        strict={true}
        warning={t("sites_deletion_warning")}
      />

      {isQrOpen && (
        <div
          className={`${isQrOpen && "fixed inset-0 flex items-center justify-center z-[999]"
            }`}
        >
          {isQrOpen && qrcode && (
            <Image
              className="relative z-[1000] cursor-pointer"
              src={qrcode}
              alt="floor qrcode"
              width={500}
              height={500}
              addBaseLink={true}
              onClick={() => handleCopyClick(qrcodeValue)}
            />
          )}
          <div
            onClick={() => {
              setIsQrOpen(false);
              setQrcode("");
            }}
            className={`bg-black ${isQrOpen ? "opacity-60" : "opacity-0 pointer-events-none"
              } absolute inset-0 z-[999] flex items-center justify-center transition-all duration-500`}
          ></div>
        </div>
      )}

      {supervisors && (
        <div
          className={`fixed top-0 left-0 w-full h-full z-[99999] bg-black bg-opacity-50 flex justify-center items-center ${supervisorModal ? "flex" : "hidden"
            }`}
          onClick={() => setSupervisorModal(null)}
        >
          <div
            className={`bg-white rounded-lg p-8 w-96 flex flex-col gap-4 min-w-[40vw] ${i18next.language === "ar" ? "rtl" : ""
              }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-center text-lg font-bold">
              {t("supervisors")}
            </h2>
            <div className="w-full overflow-x-auto">
              <table className="rounded-md w-full">
                <thead>
                  <tr
                    className={`${i18next.language == "en" ? "text-left" : "text-right"
                      }`}
                  >
                    <th className="border-2 p-4 py-2 min-w-[100px]">
                      {t("profile_pic")}
                    </th>
                    <th className="border-2 p-4 py-2 min-w-[100px]">
                      {t("name")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="w-6">
                    <td></td>
                  </tr>
                  {supervisors.map((supervisor: any, i: number) => (
                    <tr key={i}>
                      <td className="border-2 p-4 py-2 min-w-[80px]">
                        {supervisor?.user?.profile_pic ? (
                          <div
                            className={`w-16 h-16 mx-auto flex justify-center items-center gap-4 custom-tooltip hover:before:content-[attr(data-tooltip)] ${supervisor?.user?.deleted && "relative"
                              }`}
                            data-tooltip={
                              supervisor?.user?.asdc_client_user_name
                            }
                          >
                            <Image
                              className="max-h-[90%] max-w-[90%] rounded-full object-contain cursor-pointer"
                              src={supervisor?.user?.profile_pic}
                              alt={supervisor?.user?.asdc_client_user_name}
                            />
                            {supervisor?.user?.deleted && (
                              <span className="absolute top-0 left-0 w-full h-full bg-black bg-opacity-40 flex justify-center items-center text-white text-xs font-bold rounded-full">
                                {t("deleted")}
                              </span>
                            )}
                          </div>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="border-2 p-4 py-2">
                        {supervisor?.user?.asdc_client_user_name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button
              className="main_btn"
              onClick={() => setSupervisorModal(null)}
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}

      <div
        className={`fixed top-0 left-0 w-full h-full z-[100] bg-black bg-opacity-50 flex justify-center items-center ${uploadExcelModalOpen ? "flex" : "hidden"
          } ${excelLoading && "cursor-wait"}`}
      >
        <div
          className={`bg-white rounded-lg p-8 w-96 flex flex-col gap-8 relative before:absolute before:left-0 before:top-0 before:h-2 before:bg-blue-500 before:z-[1000] before:duration-[2000ms] before:ease-in-out ${i18next.language === "ar" ? "rtl" : ""
            } ${excelLoading ? "before:w-full" : "before:w-0"}`}
        >
          <h2 className="text-center text-lg font-bold">{t("upload_excel")}</h2>
          <input
            type="file"
            accept=".xlsx"
            ref={fileInputRef}
            onChange={async (e) => {
              if (!e.target.files) {
                return;
              }
              setExcelLoading(true);
              const file = e.target.files[0];
              const formData = new FormData();
              formData.append("excel_file", file);
              const response = await SendFormData(
                `excel-upload/upload-site`,
                formData,
                getValidToken()
              );
              if (response.error || !response.data) {
                setExcelLoading(false);
                handleError(response);
                if (fileInputRef?.current) {
                  (fileInputRef.current as any).value = null;
                }
                return;
              } else {
                if (response?.data?.unsuccessfulCount > 0) {
                  if (Array.isArray(response?.data?.unsuccessful)) {
                    response?.data?.unsuccessful.map((item: any) => {
                      return toast.success(`${item} in id ${item.ID}`);
                    });
                  } else {
                    toast.error("Unexpected data format received.");
                  }
                } else {
                  toast.success(t("excel_uploaded_successfully"));
                }
                setExcelLoading(false);
                setUploadExcelModalOpen(false);
                getSites(); // Refresh the sites list
                if (fileInputRef?.current) {
                  (fileInputRef.current as any).value = null;
                }
              }
            }}
          />
          <button
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
            onClick={() => {
              setUploadExcelModalOpen(false);
            }}
          >
            {t("cancel")}
          </button>
        </div>
      </div>

      {selectedSiteForMap && <StaffGPSUsersEditMap isAdmin={true} selectedSite={selectedSiteForMap} />}
    </>
  );
};

export default AdminSites;
